import Breakpoint from "@entrilia/shared/components/Breakpoint";
import ElevationHeader from "@entrilia/shared/components/ElevationHeader";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { AppBar, Box, Grid, IconButton, Stack } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMatch } from "react-router";
import useClientContext from "../../contexts/useClientContext";
import { getInvestorSettingsRoute, pageRoutes } from "../../routes";
import { identityAvatarImageSelector, investorsWithSettingsAccessSelector } from "../../store/state/user/selectors";
import MainMenu from "./mobile/MainMenu";
import PageLink from "./PageLink";
import PortalLogo from "./PortalLogo";
import PortalSwitcherButton from "./PortalSwitcherButton";
import SupportInfoHelp from "./SupportInfoHelp";
import UserMenu from "./UserMenu";

const Header = () => {
  const {
    contactName,
    isFeatureEnabled,
    portalSettings,
    portfolioWidget,
    isNewsPageAvailable,
    isDashboardPageAvailable,
  } = useClientContext();

  const avatarImage = useSelector(identityAvatarImageSelector);
  const investorsWithSettingsAccess = useSelector(investorsWithSettingsAccessSelector);

  const routeMatch = useMatch("/:client/:pageName/*");
  const pageName = routeMatch?.params["pageName"];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const containerTopPadding = useMemo(() => {
    switch (portalSettings?.logoTypeSize) {
      case "Large":
        return 0;
      case "Medium":
        return 0.5;
      default:
        return 1;
    }
  }, [portalSettings?.logoTypeSize]);

  const investorSettingsRoute = getInvestorSettingsRoute(isFeatureEnabled);

  return (
    <ElevationHeader>
      <AppBar
        position="sticky"
        sx={(theme) => ({ color: portalSettings?.appBarTextColor || theme.palette.common.white })}
      >
        <Grid
          container
          className="header"
          sx={[
            { width: "100%" },
            (theme) => ({
              bgcolor: portalSettings?.appBarColor || theme.palette.common.black,
              py: containerTopPadding,
              px: 2,
              [theme.breakpoints.down("sm")]: { py: containerTopPadding + 0.5, px: 1.5 },
            }),
          ]}
        >
          <Stack direction="row" sx={{ alignItems: "center", gap: 2, mr: 2 }}>
            <Breakpoint to="sm">
              <IconButton onClick={handleMenu} aria-label="Open menu">
                <MenuRoundedIcon />
              </IconButton>
              <MainMenu anchorEl={anchorEl} onClose={handleClose} />
            </Breakpoint>
            <Breakpoint from="sm">
              <PortalSwitcherButton />
            </Breakpoint>
            <PortalLogo />
          </Stack>
          <Breakpoint from="sm">
            <Grid container sx={{ width: "100%", flex: 1 }}>
              {isDashboardPageAvailable && (
                <PageLink
                  title="Dashboard"
                  page={pageRoutes.dashboard}
                  highlighted={pageName === pageRoutes.dashboard}
                  requiredFeature="Dashboards"
                />
              )}
              {isNewsPageAvailable && (
                <PageLink
                  title="News"
                  page={pageRoutes.news}
                  highlighted={pageName === pageRoutes.news}
                  requiredFeature="News"
                />
              )}
              <PageLink
                title={portalSettings?.inboxPageName || "Inbox"}
                page={`${pageRoutes.inbox}/Inbox/p0`}
                highlighted={pageName === pageRoutes.inbox}
                requiredFeature="Inbox"
              />
              {portfolioWidget !== undefined && (
                <PageLink
                  title="Portfolio"
                  page={pageRoutes.portfolio}
                  highlighted={pageName === pageRoutes.portfolio}
                  requiredFeature="PortfolioCompanies"
                />
              )}
              <PageLink
                title="Fundraising"
                page={pageRoutes.fundraising}
                highlighted={pageName === pageRoutes.fundraising}
                requiredFeature="Fundraising"
              />
              <PageLink
                title="Data Collection"
                page={pageRoutes.dataCollection}
                highlighted={pageName === pageRoutes.dataCollection}
                requiredFeature="DataCollection"
              />
              <HorizontalFill />
              <Box sx={{ display: "flex" }}>
                {investorSettingsRoute && investorsWithSettingsAccess.length > 0 && (
                  <PageLink
                    title="Investor Settings"
                    page={investorSettingsRoute}
                    highlighted={pageName === pageRoutes.settings}
                    icon={<SettingsIcon />}
                  />
                )}
                <SupportInfoHelp />
                <IconButton sx={{ ml: 1 }} onClick={handleMenu}>
                  <UserAvatar size="large" userName={contactName} avatarImage={avatarImage} />
                </IconButton>
              </Box>
              <UserMenu anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
            </Grid>
          </Breakpoint>
        </Grid>
      </AppBar>
    </ElevationHeader>
  );
};

export default Header;
